'use client';
import React, { useRef, useEffect, useState } from 'react';
import AppImage from './ui/AppImage';
import './ProductNew.css';

export default function ProductCard({
  product,
  onAddToCart,
  onCardClick,
  canAddToCart = true,
  showRating = true,
  titleTwoLine = false
}) {
  const cardRef = useRef(null);
  const imgRef = useRef(null);
  const [isAdded, setIsAdded] = React.useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && cardRef.current) {
            cardRef.current.classList.add('card-animate');
          }
        });
      },
      { threshold: 0.1 }
    );
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  // Mouse tilt effect
  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    cardRef.current.style.transform = `perspective(800px) rotateY(${x * 6}deg) rotateX(${-y * 6}deg) translateY(-6px)`;
  };

  const handleMouseLeave = () => {
    if (!cardRef.current) return;
    cardRef.current.style.transform = 'perspective(800px) rotateY(0deg) rotateX(0deg) translateY(0)';
    cardRef.current.style.transition = 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)';
  };

  const handleMouseEnter = () => {
    if (!cardRef.current) return;
    cardRef.current.style.transition = 'transform 0.15s ease-out';
  };

  const handleAddedToCart = (e) => {
    if (e) e.stopPropagation();
    if (!canAddToCart) return;
    setIsAdded(true);
    if (onAddToCart) onAddToCart(e);
    setTimeout(() => setIsAdded(false), 2000);
  };

  const originalPrice = Number(product.originalPrice) || Number(product.salePrice) || 0;
  const salePrice = Number(product.salePrice) || originalPrice;
  const discount = originalPrice > salePrice ? Math.round(((originalPrice - salePrice) / originalPrice) * 100) : 0;
  const isLowStock = (product.stockLeft || 99) <= 10;

  return (
    <div
      ref={cardRef}
      onClick={onCardClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
      className="opacity-100 bg-white rounded-3xl overflow-hidden shadow-card group cursor-pointer will-change-transform"
      style={{ transition: 'box-shadow 0.4s ease' }}
    >
      {/* Image container */}
      <div className="relative overflow-hidden aspect-[4/3] bg-teal/5">
        <div ref={imgRef} className="w-full h-full transition-transform duration-700 group-hover:scale-105">
          <AppImage
            src={product.image}
            alt={product.imageAlt}
            fill
            className="object-cover"
          />
        </div>

        {/* Gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-teal/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {product.badge && (
            <span className="px-2.5 py-1 rounded-full bg-teal text-white text-[10px] font-bold uppercase tracking-wider shadow-sm">
              {product.badge}
            </span>
          )}
          {product.isNew && (
            <span className="px-2.5 py-1 rounded-full bg-sage text-evergreen-deep text-[10px] font-bold uppercase tracking-wider shadow-sm">
              New
            </span>
          )}
        </div>

        {/* Discount badge */}
        {discount > 0 && (
          <div className="absolute top-3 right-3">
            <span className="w-10 h-10 rounded-full bg-teal text-clinical-white text-xs font-bold flex items-center justify-center shadow-teal-sm">
              -{discount}%
            </span>
          </div>
        )}

        {/* Low stock */}
        {isLowStock && (
          <div className="absolute bottom-3 left-3 right-3">
            <div className="glass-teal rounded-full px-3 py-1.5 flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-teal animate-pulse" />
              <span className="text-[10px] font-bold text-teal uppercase tracking-wider">
                Only {product.stockLeft} left
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Card body */}
      <div className="p-5">
        {/* Collection tag (Used as Brand now) */}
        <span className="scent-tag text-teal/70 mb-2 block">{product.brand}</span>

        <h3
          className={`text-lg font-bold text-evergreen-deep tracking-tight leading-snug mb-0.5 ${titleTwoLine ? '' : 'truncate'}`}
          title={product.name}
          style={
            titleTwoLine
              ? {
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }
              : undefined
          }
        >
          {product.name}
        </h3>
        <p className="text-xs text-slate-400 mb-2 truncate">{product.subtitle}</p>

        {/* Technical Specs (RAM/Storage) */}
        <p className="text-sm italic text-slate-500 font-medium mb-4 leading-relaxed border-l-2 border-teal/20 pl-3">
          {product.scentNote}
        </p>

        {/* Rating */}
        {showRating && (
          <div className="flex items-center gap-1.5 mb-4">
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map((star) => (
                <svg
                  key={star}
                  className={`w-3 h-3 ${star <= Math.round(product.rating) ? 'text-sage' : 'text-slate-200'}`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
            <span className="text-xs text-slate-500">{product.rating.toFixed(1)} ({product.reviews})</span>
          </div>
        )}

        {/* Price + CTA */}
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-xs text-slate-400 line-through">₹{originalPrice.toLocaleString()}</span>
            <span className="text-xl font-extrabold text-teal">₹{salePrice.toLocaleString()}</span>
          </div>

          <button
            onClick={handleAddedToCart}
            className={`product-card-cta px-4 py-2.5 text-xs uppercase tracking-widest transition-all duration-300 active:scale-95 ${
              isAdded ? 'product-card-cta--added' : ''
            }`}
            aria-label={`Add ${product.name} to ritual`}
            disabled={!canAddToCart}
          >
            <span className="button-text">
              {canAddToCart ? (isAdded ? 'Added to Cart' : 'Add to Cart') : 'Out of Stock'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
